'use client';

import {useState} from 'react';
import {ChevronDown, Trash2, Plus} from 'lucide-react';
import {FlowTextarea} from './flow_form';

const STAGE_OPTIONS = [
  'Recruiter screen',
  'Phone interview',
  'Technical screen',
  'Take-home assignment',
  'Onsite interview',
  'Bar raiser',
  'Final round',
  'Other',
];

type StructuredQuestion = {
  id: string;
  text: string;
  answer: string;
};

type StructuredStage = {
  id: string;
  name: string;
  expanded: boolean;
  questions: StructuredQuestion[];
};

function newQuestion(): StructuredQuestion {
  return {id: crypto.randomUUID(), text: '', answer: ''};
}

function newStage(name: string): StructuredStage {
  return {id: crypto.randomUUID(), name, expanded: true, questions: [newQuestion()]};
}

function serialize(stages: StructuredStage[]): string {
  return stages
    .filter((s) => s.questions.some((q) => q.text.trim()))
    .map((stage) => {
      const qs = stage.questions
        .filter((q) => q.text.trim())
        .map((q, i) => {
          const answer = q.answer.trim() ? `\n${q.answer.trim()}` : '';
          return `${i + 1}. ${q.text.trim()}${answer}`;
        })
        .join('\n\n');
      return `## ${stage.name}\n\n${qs}`;
    })
    .join('\n\n');
}

type Props = {
  onChange: (v: string) => void;
};

export function FlowStructuredQuestions({onChange}: Props) {
  const [stages, setStages] = useState<StructuredStage[]>(() => [newStage(STAGE_OPTIONS[0]!)]);
  const [addingStage, setAddingStage] = useState(false);
  const [pendingStageName, setPendingStageName] = useState(STAGE_OPTIONS[0]!);

  function update(next: StructuredStage[]) {
    setStages(next);
    onChange(serialize(next));
  }

  function toggleStage(stageId: string) {
    update(stages.map((s) => (s.id === stageId ? {...s, expanded: !s.expanded} : s)));
  }

  function addQuestion(stageId: string) {
    update(stages.map((s) => (s.id === stageId ? {...s, questions: [...s.questions, newQuestion()]} : s)));
  }

  function removeQuestion(stageId: string, qId: string) {
    update(
      stages.map((s) =>
        s.id === stageId ? {...s, questions: s.questions.filter((q) => q.id !== qId)} : s,
      ),
    );
  }

  function setQuestionField(stageId: string, qId: string, field: 'text' | 'answer', value: string) {
    update(
      stages.map((s) =>
        s.id === stageId
          ? {...s, questions: s.questions.map((q) => (q.id === qId ? {...q, [field]: value} : q))}
          : s,
      ),
    );
  }

  function confirmAddStage() {
    update([...stages, newStage(pendingStageName)]);
    setAddingStage(false);
    setPendingStageName(STAGE_OPTIONS[0]!);
  }

  return (
    <div className='flex flex-col gap-3'>
      <p className='text-sm text-iq-grey-medium'>
        Please enter the questions you were asked during each stage of the interview process.
      </p>

      {stages.map((stage) => (
        <div key={stage.id} className='rounded-xl border border-iq-grey-light bg-white overflow-hidden'>
          <button
            type='button'
            onClick={() => toggleStage(stage.id)}
            className='w-full flex items-center justify-between px-4 py-3 bg-purple-50 text-sm font-semibold text-iq-black hover:bg-purple-100 transition-colors'>
            {stage.name}
            <ChevronDown
              className={`w-4 h-4 text-iq-grey-medium transition-transform ${stage.expanded ? 'rotate-180' : ''}`}
            />
          </button>

          {stage.expanded && (
            <div className='px-4 py-4 flex flex-col gap-3'>
              {stage.questions.map((q, idx) => (
                <div key={q.id} className='flex gap-3 items-start'>
                  <span className='mt-2.5 w-5 shrink-0 text-right text-sm font-semibold text-iq-grey-medium'>
                    {idx + 1}
                  </span>
                  <div className='flex-1 flex flex-col gap-2'>
                    <input
                      type='text'
                      value={q.text}
                      onChange={(e) => setQuestionField(stage.id, q.id, 'text', e.target.value)}
                      placeholder='Question...'
                      className='w-full px-3 py-2 text-sm border border-iq-grey-light rounded-lg focus:outline-none focus:border-iq-grey-medium'
                    />
                    <FlowTextarea
                      value={q.answer}
                      onChange={(e) => setQuestionField(stage.id, q.id, 'answer', e.target.value)}
                      placeholder='Your answer (optional)...'
                      rows={3}
                    />
                  </div>
                  {stage.questions.length > 1 && (
                    <button
                      type='button'
                      onClick={() => removeQuestion(stage.id, q.id)}
                      className='mt-2.5 text-iq-grey-medium hover:text-red-500 transition-colors'>
                      <Trash2 className='w-4 h-4' />
                    </button>
                  )}
                </div>
              ))}

              <button
                type='button'
                onClick={() => addQuestion(stage.id)}
                className='flex items-center gap-2 ml-8 text-sm text-iq-grey-medium hover:text-iq-black transition-colors'>
                <div className='flex h-8 w-8 items-center justify-center rounded-lg border-2 border-dashed border-iq-grey-light'>
                  <Plus className='h-3.5 w-3.5' />
                </div>
                Add another question
              </button>
            </div>
          )}
        </div>
      ))}

      {addingStage ? (
        <div className='flex gap-2 pt-1'>
          <select
            value={pendingStageName}
            onChange={(e) => setPendingStageName(e.target.value)}
            className='flex-1 rounded-lg border border-iq-grey-light bg-white px-3 py-2 text-sm focus:border-iq-grey-medium focus:outline-none'>
            {STAGE_OPTIONS.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <button
            type='button'
            onClick={confirmAddStage}
            className='rounded-lg bg-iq-black px-3 py-2 text-sm font-medium text-white hover:opacity-80 transition-opacity'>
            Add
          </button>
          <button
            type='button'
            onClick={() => setAddingStage(false)}
            className='px-3 py-2 text-sm text-iq-grey-medium hover:text-iq-black transition-colors'>
            Cancel
          </button>
        </div>
      ) : (
        <button
          type='button'
          onClick={() => setAddingStage(true)}
          className='flex items-center gap-2 text-sm text-iq-grey-medium hover:text-iq-black transition-colors'>
          <Plus className='h-4 w-4' />
          Add interview stage
        </button>
      )}
    </div>
  );
}
